import { useEffect } from "react";
import { RiseIn } from "@/components/site/Reveal";
import { IDS } from "@/lib/testIds";

const EFFECTIVE_DATE = "27 August 2026";
const LAST_UPDATED = "28 August 2026";
const LAST_UPDATED_NOTE = "refreshed what we store on your phone, added a section on contacts and messages, rewrote notifications.";
const SUPPORT_EMAIL = "support@countersign.ca";

export default function Privacy() {
  useEffect(() => { document.title = "Privacy — Countersign"; }, []);

  return (
    <main id="main" data-testid={IDS.privacy.page}>
      <section style={{ padding: "clamp(56px, 7vw, 120px) 0 clamp(40px, 5vw, 80px)", position: "relative", overflow: "hidden" }}>
        <div className="signal-lamp" style={{ opacity: 0.6 }} />
        <div className="container-cs" style={{ position: "relative", maxWidth: 900 }}>
          <RiseIn>
            <div className="chapter-tag" style={{ marginBottom: 22 }}><span className="dot" /> Privacy</div>
          </RiseIn>
          <RiseIn delay={0.05}>
            <h1 className="font-display" style={{ fontSize: "clamp(48px, 7vw, 96px)", margin: 0, lineHeight: 1.02 }}>
              A word you send over the internet <em style={{ color: "var(--accent-ink)" }}>isn't a secret any more.</em>
            </h1>
          </RiseIn>
          <RiseIn delay={0.1}>
            <p
              data-testid="privacy-changelog"
              style={{
                marginTop: 26,
                fontSize: 16,
                lineHeight: 1.55,
                letterSpacing: "0.02em",
                color: "var(--ink-tertiary)",
                maxWidth: "70ch",
              }}
            >
              <span style={{ whiteSpace: "nowrap", letterSpacing: "0.06em" }}>
                Effective <span style={{ color: "var(--ink-secondary)" }}>{EFFECTIVE_DATE}</span>
              </span>
              {" "}
              <span aria-hidden="true" style={{ opacity: 0.55 }}>·</span>
              {" "}
              Last updated{" "}
              <span
                style={{
                  color: "var(--accent-ink)",
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontVariationSettings: '"opsz" 72, "SOFT" 20, "WONK" 0',
                  whiteSpace: "nowrap",
                }}
              >
                {LAST_UPDATED}
              </span>{" "}
              — {LAST_UPDATED_NOTE}
            </p>
          </RiseIn>
        </div>
      </section>

      <section style={{ padding: "clamp(40px, 6vw, 96px) 0 clamp(80px, 10vw, 140px)" }}>
        <div className="container-cs" style={{ maxWidth: 820 }}>
          <Article
            n="01"
            title="What we store on your phone."
            body={
              <>
                <p>Your safe word, in your device's Keychain. Your trusted contacts, including their names and phone numbers.</p>
                <p>Nothing here is sent to us, and none of it is included in an iCloud backup or copied to another device.</p>
              </>
            }
          />
          <Article
            n="02"
            title="What we never collect."
            body={
              <>
                <p>We have no account system and no sign-in. We never see your safe word, the messages you check, or who you contact.</p>
              </>
            }
          />
          <Article
            n="03"
            title="We never ask for your contacts."
            body={
              <>
                <p>Countersign does not request access to your address book. When you choose a trusted contact, iOS shows you its own picker and passes us only the one person you selected. We never see anyone else in your contacts.</p>
                <p>When you send an alert, iOS opens your Messages app with the text ready. You send it. The message never passes through us.</p>
              </>
            }
          />
          <Article
            n="04"
            title="Notifications."
            body={
              <>
                <p>If you turn on practice reminders, we use OneSignal to deliver them. That means a device identifier is sent to OneSignal, along with a small set of flags used to decide who should receive a reminder:</p>
                <ul style={{ margin: "12px 0 0", paddingLeft: 22, color: "var(--ink-secondary)", fontSize: 19, lineHeight: 1.65, listStyleType: "disc", listStylePosition: "outside" }}>
                  <li style={{ marginTop: 4 }}>whether you have set a safe word</li>
                  <li style={{ marginTop: 4 }}>whether you have added a trusted contact</li>
                  <li style={{ marginTop: 4 }}>whether reminders are switched on, and how often</li>
                  <li style={{ marginTop: 4 }}>the date you last practised</li>
                </ul>
                <p style={{ marginTop: "1.2em" }}><strong>Your safe word is never one of them. It is never included in a notification, never sent to OneSignal, and never leaves your phone.</strong></p>
              </>
            }
          />
          <Article
            n="05"
            title="Purchases."
            body={<p>Subscriptions are handled by Apple and by RevenueCat. We receive purchase status, not payment details.</p>}
          />
          <Article
            n="06"
            title="Children."
            body={<p>The app is not directed at children under 13.</p>}
          />
          <Article
            n="07"
            title="Changes."
            body={
              <>
                <p>If we change this policy in a way that matters, we'll say so on this page and update the effective date. If the change is meaningful, we'll also mention it inside the app.</p>
              </>
            }
          />
          <Article
            n="08"
            title="Contact."
            body={
              <>
                <p>Write to <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-link" style={{ color: "var(--accent-ink)" }}>{SUPPORT_EMAIL}</a>. A real person reads it.</p>
              </>
            }
          />
        </div>
      </section>
    </main>
  );
}

function Article({ n, title, body }) {
  return (
    <RiseIn>
      <article style={{ padding: "clamp(28px, 3.4vw, 44px) 0", borderTop: "1px solid var(--separator)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 12 }} className="pv-row">
          <div className="font-display" style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 20, color: "var(--accent-ink)", fontVariationSettings: '"opsz" 72, "SOFT" 30, "WONK" 0' }}>
            {n}
          </div>
          <div>
            <h2 className="font-display" style={{ fontSize: "clamp(26px, 3vw, 40px)", margin: 0 }}>{title}</h2>
            <div className="prose-cs" style={{ marginTop: 16 }}>{body}</div>
          </div>
        </div>
        <style>{`
          @media (min-width: 720px) {
            .pv-row { grid-template-columns: 80px 1fr !important; align-items: start; gap: 32px; }
          }
        `}</style>
      </article>
    </RiseIn>
  );
}
